import React, { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import Logo from '../../images/gattomioNero.png';
import { getUserOrders } from '../../api/backend';

const statusConfig = {
  pending: { label: 'In attesa', badgeClass: 'bg-warning text-dark' },
  processing: { label: 'In elaborazione', badgeClass: 'bg-info text-white' },
  completed: { label: 'Completato', badgeClass: 'bg-success text-white' },
  shipped: { label: 'Spedito', badgeClass: 'bg-primary text-white' },
  cancelled: { label: 'Annullato', badgeClass: 'bg-danger text-white' },
};

const MyOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: '', sort: 'date_desc' });
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 });

  const fetchOrders = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, sort: filters.sort };
      if (filters.status) params.status = filters.status;
      const response = await getUserOrders(params);
      setOrders(response.data || []);
      setPagination({
        current_page: response.current_page || 1,
        last_page: response.last_page || 1,
        total: response.total || 0,
      });
    } catch (err) {
      setError('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return '€ ' + parseFloat(amount || 0).toFixed(2).replace('.', ',');
  };

  const getOrderNumber = (id) => `ORD-${String(id).padStart(6, '0')}`;

  return (
    <Fragment>
      <Navbar hclass={'wpo-site-header'} Logo={Logo} />
      <PageTitle pageTitle={'I miei ordini'} pagesub={'Ordini'} />
      <section className="wpo-orders-section section-padding">
        <div className="container">
          <div className="orders-header">
            <span className="panel-header">I tuoi acquisti</span>
            <h2>I miei ordini</h2>
          </div>

          {/* Filtri */}
          <div className="orders-filters">
            <div className="filter-group">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">Tutti gli stati</option>
                <option value="pending">In attesa</option>
                <option value="processing">In elaborazione</option>
                <option value="completed">Completato</option>
                <option value="shipped">Spedito</option>
                <option value="cancelled">Annullato</option>
              </select>
            </div>
            <div className="filter-group">
              <select
                value={filters.sort}
                onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
              >
                <option value="date_desc">Data (più recenti)</option>
                <option value="date_asc">Data (più vecchi)</option>
                <option value="amount_desc">Importo (decrescente)</option>
                <option value="amount_asc">Importo (crescente)</option>
              </select>
            </div>
            <span className="filter-info">{pagination.total} ordini totali</span>
          </div>

          {/* Stato loading */}
          {loading && (
            <div className="orders-loading">
              <div className="spinner">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Caricamento...</span>
                </div>
              </div>
              <p>Caricamento ordini...</p>
            </div>
          )}

          {/* Errore */}
          {error && (
            <div className="orders-alert">
              <i className="ti-alert alert-icon"></i>
              <div className="alert-message">{error}</div>
              <div className="alert-action">
                <button onClick={() => fetchOrders()}>Riprova</button>
              </div>
            </div>
          )}

          {/* Lista vuota */}
          {!loading && !error && orders.length === 0 && (
            <div className="orders-empty">
              <div className="empty-icon">
                <i className="fi flaticon-box"></i>
              </div>
              <h4>Nessun ordine trovato</h4>
              <p>Non hai ancora effettuato acquisti.</p>
              <Link to="/shop" className="empty-btn">Vai allo shop</Link>
            </div>
          )}

          {/* Lista ordini */}
          {!loading && !error && orders.length > 0 && (
            <>
              <div className="orders-table">
                <div className="table-responsive">
                  <table>
                    <thead>
                      <tr>
                        <th>Numero ordine</th>
                        <th>Data</th>
                        <th>Prodotti</th>
                        <th>Totale</th>
                        <th>Stato</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => {
                        const statusKey = order.status || 'pending';
                        const status = statusConfig[statusKey] || { label: order.status, badgeClass: 'status-pending' };
                        const itemsCount = order.order_products?.reduce((sum, p) => sum + p.quantity, 0) || 0;
                        return (
                          <tr key={order.id}>
                            <td><strong>{getOrderNumber(order.id)}</strong></td>
                            <td>{formatDate(order.created_at)}</td>
                            <td>{itemsCount} {itemsCount === 1 ? 'prodotto' : 'prodotti'}</td>
                            <td><strong>{formatAmount(order.amount)}</strong></td>
                            <td>
                              <span className={`badge status-${statusKey}`}>{status.label}</span>
                            </td>
                            <td>
                              <Link to={`/orders/${order.id}`} className="btn-details">
                                Dettagli
                              </Link>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Paginazione */}
              {pagination.last_page > 1 && (
                <div className="orders-pagination">
                  <nav>
                    <ul className="pagination">
                      {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
                        <li key={page} className={`page-item ${page === pagination.current_page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => fetchOrders(page)}>{page}</button>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      <Footer />
      <Scrollbar />
    </Fragment>
  );
};

export default MyOrdersPage;
